/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 10:58:20 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormControl } from "@mui/material";
import { OSActionButton } from "ui-widgets";
import { PrimaryKeyValue } from "lib/types";
import { useControlledInput } from "ui-widgets/forms";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { FormSubmitBubblingStopper } from "ui-widgets/forms/FormSubmitBubblingStopper";
import {
  APIFormRelatedPopupInputWrapper,
  GenericAPIRelatedFormPopupInputProps,
} from "ui-widgets/forms/input-fields/related/APIFormRelatedPopupInputWrapper";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { StyledOSDialog } from "ui-widgets/dialogs/GenericDialogs";

interface PopUpFormRendePropSpec<RelatedObject> {
  foreignKeyID: PrimaryKeyValue<RelatedObject>;
  updatePrimaryObject: (updatedForeignKeyID: PrimaryKeyValue<RelatedObject>) => void;
  handleClose: () => void;
}

type APIMany2OnePopupFormInputProps<PrimaryObject, RelatedObject> = GenericAPIRelatedFormPopupInputProps<
  PrimaryObject,
  RelatedObject
> & {
  renderRelatedObjectForm: (args: PopUpFormRendePropSpec<RelatedObject>) => JSX.Element;
};

export const APIMany2OnePopupFormInput = <PrimaryObject, RelatedObject>(
  props: APIMany2OnePopupFormInputProps<PrimaryObject, RelatedObject>
) => {
  const { open, handleClose, handleOpen } = useToggleState();

  const {
    field: { onChange, value },
  } = useControlledInput<PrimaryObject>(props);

  const useAPIFormReturn = useOSAPIFormContext<PrimaryObject>();

  // foreignKeyID is stateful because hook form setValues is called in the useEffect hook in OSAPI form component.
  // Note: setValues and getValues do not do re-renders so without a useEffect
  const foreignKeyID = value;

  const updatePrimaryObject = (updatedForeignKeyID: PrimaryKeyValue<RelatedObject>) => {
    onChange(updatedForeignKeyID);
    useAPIFormReturn?.submitAPIForm(); // AutoSave the Main form when this related record is saved or edited
    handleClose();
  };

  return (
    <APIFormRelatedPopupInputWrapper {...props}>
      <FormControl>
        <OSActionButton
          variant={props.variant ?? "outlined"}
          onClick={handleOpen}
          actionStyle={foreignKeyID ? "edit" : "add"}
        >
          {`${foreignKeyID ? "Edit" : "Add"} ${props.label}`}
        </OSActionButton>
        <FormSubmitBubblingStopper>
          <StyledOSDialog
            popUpTitle={props.popUpTitle}
            popUpDescription={props.popUpDescription}
            open={open}
            onClose={handleClose}
          >
            {props.renderRelatedObjectForm({ foreignKeyID, updatePrimaryObject, handleClose })}
          </StyledOSDialog>
        </FormSubmitBubblingStopper>
      </FormControl>
    </APIFormRelatedPopupInputWrapper>
  );
};
